class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.cartItems = [];
    this.upsellProducts = [];
    this.isLoading = false;
    this.cartUpdateUnsubscriber = undefined;
    
    // Get settings from data attributes
    this.settings = {
      heading: this.dataset.heading || 'Complete Your Order',
      subtitle: this.dataset.subtitle || 'Customers also bought these items',
      showSubtitle: this.dataset.showSubtitle === 'true',
      imageRatio: this.dataset.imageRatio || 'square',
      showViewAll: this.dataset.showViewAll === 'true',
      viewAllText: this.dataset.viewAllText || 'View All Products',
      viewAllCollection: this.dataset.viewAllCollection || ''
    };
    
    this.init();
  }

  init() {
    this.updateHeader();
    this.addViewAllButton();
    this.bindEvents();
  }

  bindEvents() {
    // Listen for cart updates from the main cart system
    if (typeof subscribe !== 'undefined' && PUB_SUB_EVENTS) {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
    }
    
    // Listen for form submissions within upsell items
    this.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleCartUpdate(event) {
    // Update cart items from the event
    if (event.cartData && event.cartData.items) {
      this.cartItems = event.cartData.items;
      this.updateUpsells();
    }
  }

  async updateUpsells() {
    if (this.cartItems.length === 0) {
      this.hide();
      return;
    }

    this.show();
    this.setLoading(true);

    try {
      // Get product IDs from cart
      const productIds = this.cartItems.map(item => item.product_id);
      
      // Fetch recommendations using Shopify's product recommendations API
      const recommendations = await this.fetchRecommendations(productIds);
      
      if (recommendations.length > 0) {
        this.renderUpsells(recommendations);
      } else {
        this.hide();
      }
    } catch (error) {
      console.error('Error updating upsells:', error);
      this.hide();
    } finally {
      this.setLoading(false);
    }
  }

  async fetchRecommendations(productIds) {
    if (productIds.length === 0) return [];

    // Use the first product ID for recommendations
    const productId = productIds[0];
    const url = `/recommendations/products.json?product_id=${productId}&limit=4`;
    
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return [];
    }
  }

  renderUpsells(products) {
    const grid = this.querySelector('.cart-upsell-grid');
    if (!grid) return;

    grid.innerHTML = products.map(product => this.renderUpsellItem(product)).join('');
  }

  renderUpsellItem(product) {
    const variant = product.variants[0];
    const isAvailable = variant && variant.available;
    
    return `
      <div class="cart-upsell-item">
        <div class="cart-upsell-item__media">
          <img 
            src="${product.featured_image || product.images[0]}"
            alt="${product.title}"
            width="80"
            height="80"
            loading="lazy"
          >
        </div>
        <div class="cart-upsell-item__content">
          <h4 class="cart-upsell-item__title">
            <a href="${product.url}">${product.title}</a>
          </h4>
          <div class="cart-upsell-item__price">
            ${this.renderPrice(product)}
          </div>
          <form class="cart-upsell-item__form" action="/cart/add" method="post" enctype="multipart/form-data">
            <input type="hidden" name="id" value="${variant ? variant.id : ''}">
            <button type="submit" class="cart-upsell-item__button" ${!isAvailable ? 'disabled' : ''}>
              ${isAvailable ? 'Add to Cart' : 'Sold Out'}
            </button>
          </form>
        </div>
      </div>
    `;
  }

  renderPrice(product) {
    const variant = product.variants[0];
    if (!variant) return '';

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      return `
        <span class="price__sale">${this.formatMoney(variant.price)}</span>
        <span class="price__compare">${this.formatMoney(variant.compare_at_price)}</span>
      `;
    } else {
      return `<span class="price__regular">${this.formatMoney(variant.price)}</span>`;
    }
  }

  formatMoney(cents) {
    // Simple money formatting - you might want to use Shopify's money format
    return `$${(cents / 100).toFixed(2)}`;
  }

  handleFormSubmit(event) {
    if (event.target.classList.contains('cart-upsell-item__form')) {
      event.preventDefault();
      
      const form = event.target;
      const button = form.querySelector('.cart-upsell-item__button');
      const originalText = button.textContent;
      
      // Show loading state
      button.textContent = 'Adding...';
      button.disabled = true;
      
      const formData = new FormData(form);
      
      // Get the cart drawer
      const cart = document.querySelector('cart-drawer');
      
      if (cart) {
        // Add sections to form data for cart rendering
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      
      // Add to cart using the same approach as product-form
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;
      
      console.log('Adding to cart via upsell:', formData.get('id'));
      
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          console.log('Cart add response:', response);
          
          if (response.status) {
            // Handle error
            console.error('Cart add error:', response);
            this.showErrorMessage(button, originalText);
            return;
          }
          
          // Success - publish cart update event
          if (typeof publish !== 'undefined' && PUB_SUB_EVENTS) {
            console.log('Publishing cart update event');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-upsell',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }
          
          // Render cart contents
          if (cart && typeof cart.renderContents === 'function') {
            console.log('Rendering cart contents');
            cart.renderContents(response);
          }
          
          // Show success feedback
          this.showAddToCartSuccess(button, originalText);
        })
        .catch((error) => {
          console.error('Error adding to cart:', error);
          this.showErrorMessage(button, originalText);
        });
    }
  }

  showAddToCartSuccess(button, originalText) {
    button.textContent = 'Added!';
    button.style.backgroundColor = 'var(--color-success, #28a745)';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
      button.disabled = false;
    }, 1500);
  }

  showErrorMessage(button, originalText) {
    button.textContent = 'Error';
    button.style.backgroundColor = 'var(--color-error, #dc3545)';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
      button.disabled = false;
    }, 2000);
  }

  updateHeader() {
    const titleElement = this.querySelector('.cart-upsell-title');
    const subtitleElement = this.querySelector('.cart-upsell-subtitle');
    
    if (titleElement) {
      titleElement.textContent = this.settings.heading;
    }
    
    if (subtitleElement) {
      if (this.settings.showSubtitle) {
        subtitleElement.textContent = this.settings.subtitle;
        subtitleElement.style.display = 'block';
      } else {
        subtitleElement.style.display = 'none';
      }
    }
  }

  addViewAllButton() {
    if (!this.settings.showViewAll || !this.settings.viewAllCollection) {
      return;
    }

    // Remove existing view all button if present
    const existingButton = this.querySelector('.cart-upsell-footer');
    if (existingButton) {
      existingButton.remove();
    }

    const footer = document.createElement('div');
    footer.className = 'cart-upsell-footer';
    footer.innerHTML = `
      <a href="/collections/${this.settings.viewAllCollection}" class="button">
        ${this.settings.viewAllText}
      </a>
    `;

    const section = this.querySelector('.cart-upsell-section');
    if (section) {
      section.appendChild(footer);
    }
  }

  show() {
    this.style.display = 'block';
  }

  hide() {
    this.style.display = 'none';
  }

  setLoading(loading) {
    this.isLoading = loading;
    const section = this.querySelector('.cart-upsell-section');
    
    if (section) {
      if (loading) {
        section.classList.add('loading');
      } else {
        section.classList.remove('loading');
      }
    }
  }
}

customElements.define('cart-upsell', CartUpsell);
